import type { LostCall, LostCallCheck } from "@/lib/types";
import { formatDuration } from "@/lib/utils";

/**
 * The words of the Calls page's "Never reached us" (platform ADR-178): what the carrier said
 * about a call, what happened with the caller since, and whether the list can be trusted.
 *
 * The rows come from the carrier's log, not the switch: a call is listed because the switch never
 * took it, or because the carrier ended it within two seconds. So the sentence for a row says what
 * the carrier saw, and the page says plainly when the check is off or failing, since an empty list
 * there would otherwise read as "nothing lost".
 */

export type Tone = "danger" | "warning" | "neutral";

/** What the carrier said about the call: a short label for the badge, and the sentence. */
export function carrierSaid(call: Pick<LostCall, "carrier_status" | "carrier_seconds" | "reached_switch">): {
  label: string;
  detail: string;
  tone: Tone;
} {
  const seconds = formatDuration(call.carrier_seconds);
  switch (call.carrier_status) {
    case "failed":
      return { label: "Failed", detail: "The carrier could not put it through to us.", tone: "danger" };
    case "busy":
      return { label: "Busy", detail: "Our side answered busy.", tone: "danger" };
    case "no-answer":
      return { label: "Not answered", detail: "Our side never answered it.", tone: "danger" };
    case "canceled":
      return {
        label: "Hung up early",
        detail: "The caller hung up before our side answered.",
        tone: "warning",
      };
  }
  if (call.reached_switch) {
    return {
      label: `Cut off at ${seconds}`,
      detail: `Our switch took it, and it was cut off after ${seconds}.`,
      tone: "danger",
    };
  }
  if (call.carrier_seconds < 2) {
    return {
      label: `Cut off at ${seconds}`,
      detail: `Cut off after ${seconds}. Nothing of ours saw it.`,
      tone: "danger",
    };
  }
  return {
    label: "No record of ours",
    detail: `The carrier connected it for ${seconds}, and our switch has no record of it.`,
    tone: "warning",
  };
}

/** Who called, for a row: the contact's name when the number is one of ours, else the number. */
export function lostCallWho(
  call: Pick<LostCall, "hidden" | "lead_name" | "caller_number">,
  formatNumber: (n: string) => string,
): string {
  if (call.hidden) return "Hidden number";
  return call.lead_name ?? formatNumber(call.caller_number);
}

/** What happened with the caller since, or "Not yet": the first call either way. */
export function followUpText(
  call: Pick<LostCall, "follow_up" | "hidden">,
  formatTime: (iso: string) => string,
): { text: string; done: boolean } {
  if (call.follow_up?.kind === "called_back") {
    return { text: `Called back ${formatTime(call.follow_up.at)}`, done: true };
  }
  if (call.follow_up?.kind === "called_in") {
    return { text: `Called in again ${formatTime(call.follow_up.at)}`, done: true };
  }
  return { text: call.hidden ? "Cannot be called back" : "Not yet", done: false };
}

/**
 * Whether the list can be trusted, said over it. Off: nothing is ever listed. A failed last read,
 * or a last read long ago: calls may be missing. Otherwise how often it is read, and when it was.
 */
export function checkNotice(
  check: LostCallCheck,
  now: number,
  formatTime: (iso: string) => string,
): { tone: "warning" | "neutral"; text: string } {
  if (!check.on) {
    return {
      tone: "warning",
      text:
        "The carrier check is not turned on for this server, so nothing is listed here even " +
        "when a call is lost.",
    };
  }
  const every = check.every_seconds ?? 300;
  const minutes = Math.max(1, Math.round(every / 60));
  const often = minutes === 1 ? "every minute" : `every ${minutes} minutes`;
  const last = check.checked_at ? ` Last read ${formatTime(check.checked_at)}.` : "";
  if (check.problem) {
    return {
      tone: "warning",
      text: `The last read of the carrier's log failed: ${check.problem}${last}`,
    };
  }
  if (!check.checked_at) {
    return { tone: "neutral", text: `The carrier's log is read ${often}. Not read yet.` };
  }
  const age = now - Date.parse(check.checked_at);
  if (age > every * 3 * 1000) {
    return {
      tone: "warning",
      text:
        `The carrier's log has not been read since ${formatTime(check.checked_at)}; it should ` +
        `be ${often}, so recent calls may be missing.`,
    };
  }
  return {
    tone: "neutral",
    text: `The carrier's log is read ${often}.${last}`,
  };
}
